import {DataStatus} from 'platform/components';
import {Integer, Show} from 'platform/foundation';
import styled, {createGlobalStyle} from 'styled-components';

import {useMemo} from 'react';
import {Outlet, useLocation} from 'react-router-dom';

import {
  AI_ASSISTANT_SIDEBAR_WIDTH_CSS_VARIABLE,
  getAiAssistantSidebarWidthCss,
  useAiAssistantPresentation,
} from '@dms/ai-assistant';
import {useGetCurrentUserInfoQuery, useGetUserMenuPinsQuery} from '@dms/api/customer';
import {MenuItemProps} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import {PerformancePanel} from '@dms/dev-settings';
import {TelephonyScreenPopStrip, useCallPanelDock} from '@dms/features/telephony-call';
import {MAIN_SIDEBAR_WIDTH, MAIN_TOPBAR_HEIGHT} from '@dms/shared';

import {RequiredTestIdProps} from 'shared/web';

import {AppContainer} from './components/AppContainer';
import {PageNavigationBar} from './components/PageNavigationBar';
import {ShowOnMobileNavigation} from './components/ShowOnMobileNavigation';
import {Sidebar} from './components/Sidebar';
import {TopBar} from './components/TopBar';
import {AI_ASSISTANT_GUTTER_TRANSITION} from './constants/AI_ASSISTANT_GUTTER_TRANSITION';
import {APP_CONTAINER_ID} from './hooks/useAppContainerRef';
import {useIsPageNavigationVisible} from './hooks/useIsPageNavigationVisible';
import {useRefreshCommunicationUnreadState} from './hooks/useRefreshCommunicationUnreadState';
import {isMenuItemActive} from './utils/isMenuItemActive';

const isDev = process.env.ENV_TYPE === 'dev';

interface LayoutProps extends RequiredTestIdProps {
  menuItems: MenuItemProps[];
  menuItemsById: Record<string, MenuItemProps>;
  createMenuItems: MenuItemProps[];
}

export function Layout(props: LayoutProps) {
  const location = useLocation();
  const isPageNavigationVisible = useIsPageNavigationVisible();
  useRefreshCommunicationUnreadState();
  const assistantPresentation = useAiAssistantPresentation();
  const callPanelDock = useCallPanelDock();

  const {
    data: userInfo,
    isLoading: isUserInfoLoading,
    isError: isUserInfoErrored,
  } = useGetCurrentUserInfoQuery();
  const {isLoading: isPinnedMenuItemsLoading, isError: isPinnedMenuItemsErrored} =
    useGetUserMenuPinsQuery();

  const settingsModule = props.menuItems.find((item) => item.id === moduleKeys.settings);
  const activeModule = useMemo(
    () => props.menuItems.find((menuItem) => isMenuItemActive(location.pathname, menuItem.path)),
    [location.pathname, props.menuItems]
  );

  return (
    <DataStatus
      minHeight="100%"
      isLoading={isPinnedMenuItemsLoading || isUserInfoLoading}
      isError={isPinnedMenuItemsErrored || isUserInfoErrored}
      data={userInfo}
    >
      {({data}) => (
        <>
          <AssistantGutterGlobalStyle
            $isAssistantSidebarOpen={assistantPresentation.isSidebarOpen}
            $assistantSidebarWidth={assistantPresentation.sidebarWidth}
          />
          <Show when={callPanelDock.dockedWidth}>
            {(width) => <DockedPanelGlobalStyle $width={width} />}
          </Show>
          <LayoutGrid $isAssistantSidebarResizing={assistantPresentation.isSidebarResizing}>
            <Sidebar
              iconSize={data.settings.sidebarIconSize}
              menuItems={props.menuItems}
              menuItemsById={props.menuItemsById}
              createMenuItems={props.createMenuItems}
              data-testid="sidebar"
            />

            <Show when={isDev}>
              <PerformancePanel />
            </Show>

            <TopBar
              iconSize={data.settings.sidebarIconSize}
              settingsModule={settingsModule}
              activeModule={activeModule}
              data-testid="header"
            />
            <ShowOnMobileNavigation when={isPageNavigationVisible}>
              <PageNavigationBar data-testid="sub-bar" />
            </ShowOnMobileNavigation>

            <AppContainer>
              <TelephonyScreenPopStrip data-testid="layout" />
              <Outlet />
            </AppContainer>
          </LayoutGrid>
        </>
      )}
    </DataStatus>
  );
}

const AssistantGutterGlobalStyle = createGlobalStyle<{
  $isAssistantSidebarOpen: boolean;
  $assistantSidebarWidth: number;
}>`
  :root {
    ${AI_ASSISTANT_SIDEBAR_WIDTH_CSS_VARIABLE}: ${({$assistantSidebarWidth}) =>
      getAiAssistantSidebarWidthCss($assistantSidebarWidth)};
    --dms-assistant-gutter: 0;

    @media ${({theme}) => theme.devices.notebook}, ${({theme}) => theme.devices.desktop} {
      --dms-assistant-gutter: ${({$isAssistantSidebarOpen}) =>
        $isAssistantSidebarOpen ? `var(${AI_ASSISTANT_SIDEBAR_WIDTH_CSS_VARIABLE})` : 0};
    }
  }
`;

/** The docked call panel sits under the top bar, so it narrows only the page, not the grid. */
const DockedPanelGlobalStyle = createGlobalStyle<{$width: Integer}>`
  @media ${({theme}) => theme.devices.notebook}, ${({theme}) => theme.devices.desktop} {
    [id='${APP_CONTAINER_ID}'] {
      width: calc(100% - ${({theme, $width}) => theme.getSize($width)});
    }
  }
`;

const LayoutGrid = styled.div<{$isAssistantSidebarResizing: boolean}>`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: ${({theme}) => theme.getSize(MAIN_SIDEBAR_WIDTH)} 1fr;
  grid-template-rows: ${({theme}) => theme.getSize(MAIN_TOPBAR_HEIGHT)} 1fr;
  transition: ${({$isAssistantSidebarResizing}) =>
    $isAssistantSidebarResizing ? 'none' : AI_ASSISTANT_GUTTER_TRANSITION};

  @media ${({theme}) => theme.devices.notebook}, ${({theme}) => theme.devices.desktop} {
    padding-right: var(--dms-assistant-gutter, 0);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
